import { useQuery } from '@tanstack/react-query';
import {
  Action,
  Alert,
  Badge,
  Checkbox,
  Icon,
  NumberInput,
  OrderLineItem,
  SimpleSelect,
  SpinnerIcon,
  TextInput,
} from 'corso-admin-ui';
import { formatter } from 'corso-web-lib';
import { useCallback, useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import api from '~/api';
import ProductImage from '~/components/ProductImage';
import { useCurrency } from '~/hooks/useCurrency';
import { useStoreId } from '~/providers/StoreUserProvider';
import { SelectedVariant, Variant } from '~/types';

type FulfillmentLocation = {
  idFromPlatform: string;
  name: string;
};

function ProductVariantInfo({ variant }: { variant: Variant }) {
  const { currencyCode } = useCurrency();
  const { quantity, price } = variant;

  return (
    <>
      <p className="text-right text-xs tabular-nums">
        {formatter.count(quantity)} available
      </p>
      <p className="text-right text-xs tabular-nums">
        {formatter.currency(price, currencyCode)}
      </p>
    </>
  );
}

/**
 * Creates a helper to toggle selected variants via adding, updating, or removing.
 */
function useToggleSelectedVariants(selectedVariants: SelectedVariant[]) {
  const currentSelections = useMemo(
    () =>
      new Map(
        selectedVariants
          .filter((v) => !!v.quantity)
          .map((variant) => [variant.idFromPlatform, variant] as const),
      ),
    [selectedVariants],
  );

  /** For each variant, add or update it if quantity is not zero; otherwise, remove it if it exits. */
  const toggleVariants = useCallback(
    (variants: SelectedVariant[]) => {
      variants.forEach((variant) => {
        if (variant.quantity === 0)
          currentSelections.delete(variant.idFromPlatform);
        else currentSelections.set(variant.idFromPlatform, variant);
      });

      return Array.from(currentSelections.values());
    },
    [currentSelections],
  );

  return [currentSelections, toggleVariants] as const;
}

function ProductSearch({
  defaultSearchTerm = '',
  selectedVariants,
  onChange,
  disabled = false,
  limited = false,
  canSelectProducts = false,
  defaultQuantity = 1,
  locations,
  allLocationsLabel,
}: {
  defaultSearchTerm?: string;
  selectedVariants: SelectedVariant[];
  onChange: (selectedVariants: SelectedVariant[]) => void;
  /** Disables all checkboxes. */
  disabled?: boolean;
  /** Acts to disable checkboxes that aren't selected. */
  limited?: boolean;
  allLocationsLabel?: string;

  locations: FulfillmentLocation[];

  /** If a product has multiple variants, allows all to be selected. */
  canSelectProducts?: boolean; // TODO better name

  /** Default quantity when adding an item. */
  defaultQuantity?: number;
}) {
  const storeId = useStoreId();

  const locationOptions = locations.map((location) => ({
    value: location.idFromPlatform,
    label: location.name,
  }));

  const locationAndAllOptions = [
    {
      value: 'all',
      label: allLocationsLabel ?? 'All Locations',
    },
  ].concat(locationOptions.sort((a, b) => a.label.localeCompare(b.label)));

  const [searchTerm, setSearchTerm] = useDebounceValue(defaultSearchTerm, 300);
  const [selectedLocationId, setSelectedLocationId] = useDebounceValue(
    'all',
    300,
  );

  const [currentSelections, toggleVariants] =
    useToggleSelectedVariants(selectedVariants);

  const handleChange = useCallback(
    (select: SelectedVariant[]) => onChange(toggleVariants(select)),
    [onChange, toggleVariants],
  );

  const fulfillmentLocationIdFromPlatform =
    selectedLocationId === 'all' ? undefined : selectedLocationId;

  const { data: products, isLoading } = useQuery({
    queryKey: [
      'product-search',
      { storeId, searchTerm, fulfillmentLocationIdFromPlatform },
    ],
    queryFn: () =>
      api.store(storeId).product.search({
        searchTerm,
        fulfillmentLocationIdFromPlatform,
      }),
  });

  const locationSelected = fulfillmentLocationIdFromPlatform !== undefined;

  const isSelected = useCallback(
    (variantOrVariants: Variant | Variant[]) => {
      const variants = Array.isArray(variantOrVariants)
        ? variantOrVariants
        : [variantOrVariants];

      const selectedVariantIds = variants.map(
        (variant) => variant.idFromPlatform,
      );
      const currentVariants = selectedVariantIds.map((id) =>
        currentSelections.get(id),
      );

      const allSelected = currentVariants.every(
        (variant) => variant && variant.quantity > 0,
      );
      return allSelected;
    },
    [currentSelections],
  );

  // ? might need to virtualize list in the future
  return (
    <div className="space-y-2">
      <TextInput
        id="product-search"
        placeholder="Search products"
        label="Search Products"
        labelVisuallyHidden
        defaultValue={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        addon={{
          insideStart: <Icon icon="search" className="size-3" />,

          // ? add inline action to clear search
        }}
      />

      <SimpleSelect
        label="Location"
        labelVisuallyHidden
        options={locationAndAllOptions}
        value={selectedLocationId}
        onChange={setSelectedLocationId}
      />

      {locationSelected && (
        <Alert variant="info" heading="Inventory is Informational">
          Location inventory won&apos;t force fulfillment there. Shopify&apos;s
          Order Routing rules still decide where orders ship from.
        </Alert>
      )}

      <div className="relative h-80 overflow-y-auto scroll-smooth border-y border-neutral-200">
        {isLoading && (
          <div className="flex size-full flex-col items-center justify-center">
            <SpinnerIcon accessibilityLabel="Loading" className="size-8" />
          </div>
        )}
        {!products?.length && (
          <div className="flex size-full flex-col items-center justify-center gap-1 text-sm">
            <Icon icon="search" className="size-4" />
            <p className="font-medium">No products found</p>
            <p className="text-neutral-500">
              Try searching for a different product
            </p>
          </div>
        )}
        {!!products?.length && (
          <ul>
            {products.map((product) => (
              <li key={product.id}>
                <label className="sticky top-0 grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-4 border-b border-neutral-200 bg-white p-2 hover:bg-neutral-100">
                  <div className="flex items-center gap-2">
                    {canSelectProducts || product.variants.length === 1 ? (
                      <Checkbox
                        indeterminate={
                          !isSelected(product.variants) &&
                          product.variants.some((variant) =>
                            isSelected(variant),
                          )
                        }
                        checked={isSelected(product.variants)}
                        disabled={
                          disabled || (limited && !isSelected(product.variants))
                        }
                        onChange={() => {
                          const selected = isSelected(product.variants); // could alternatively use the value of the checkbox changed
                          const quantity = selected ? 0 : defaultQuantity;

                          handleChange(
                            product.variants.map((variant) => ({
                              ...variant,
                              quantity,
                            })),
                          );
                        }}
                      />
                    ) : null}
                    <ProductImage
                      src={product.imgUrl}
                      alt={product.title}
                      className="size-10"
                    />
                    <p className="text-xs">{product.title}</p>
                  </div>
                  {product.variants.length === 1 && product.variants[0] && (
                    <ProductVariantInfo variant={product.variants[0]} />
                  )}
                </label>
                {product.variants.length > 1 && (
                  <ul>
                    {product.variants.map((variant) => (
                      <li key={variant.id}>
                        {/* oxlint-disable-next-line jsx-a11y/label-has-associated-control -- linked to checkbox, but the rule just isn't picking it up */}
                        <label className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-4 border-b border-neutral-200 bg-white p-2 pl-6 hover:bg-neutral-100">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={isSelected(variant)}
                              disabled={
                                disabled || (limited && !isSelected(variant))
                              }
                              onChange={() => {
                                const selected = isSelected(variant);
                                const quantity = selected ? 0 : 1;

                                handleChange([
                                  {
                                    ...variant,
                                    quantity,
                                  },
                                ]);
                              }}
                            />
                            <p className="text-xs">
                              {variant.optionsValueString}
                            </p>
                          </div>
                          <ProductVariantInfo variant={variant} />
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SelectedVariantsList({
  selectedVariants,
  onChange,
  quantityLimit,
}: {
  selectedVariants: SelectedVariant[];
  onChange: (selectedVariants: SelectedVariant[]) => void;
  quantityLimit?: number;
}) {
  const { currencyCode } = useCurrency();
  const [currentSelections, toggleVariants] =
    useToggleSelectedVariants(selectedVariants);

  const handleChange = useCallback(
    (select: SelectedVariant[]) => onChange(toggleVariants(select)),
    [onChange, toggleVariants],
  );

  if (currentSelections.size === 0) return null;

  // table-like display leveraging subgrid for fine control with header // ? could be interesting to make a table but entirely replace it with grid/subgrid
  return (
    <section className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
      <header className="col-span-full grid grid-cols-subgrid items-center gap-4">
        <p className="text-sm font-semibold">Product</p>
        <p className="text-sm font-semibold">Quantity</p>
        <p className="hidden text-sm font-semibold sm:block">Total</p>
      </header>
      <ul className="col-span-full grid grid-cols-subgrid gap-2">
        {selectedVariants.map((selectedVariant) => (
          <li
            key={selectedVariant.idFromPlatform}
            className="col-span-full grid grid-cols-subgrid items-center gap-4"
          >
            <OrderLineItem
              title={selectedVariant.title}
              sku={selectedVariant.sku}
              options={selectedVariant.options}
              src={selectedVariant.imgUrl}
            />
            <NumberInput
              id={`quantity-${selectedVariant.idFromPlatform}`}
              label="Quantity"
              labelVisuallyHidden
              className="w-[8ch]" // 3 digits + 5 for up/down and spacing
              value={selectedVariant.quantity}
              required
              disabled={quantityLimit === 1}
              step={1}
              min={1}
              max={quantityLimit ?? 9999} // may need to remove, limits based on the available visual, but also a reasonable base limit
              onChange={(e) =>
                handleChange([
                  { ...selectedVariant, quantity: e.target.valueAsNumber },
                ])
              }
            />
            <p className="hidden text-sm tabular-nums sm:block">
              {formatter.currency(
                selectedVariant.price * selectedVariant.quantity,
                currencyCode,
              )}
            </p>
            <Action
              variant="ghost"
              icon="close"
              accessibilityLabel={`Remove ${selectedVariant.title}`}
              onClick={() => {
                handleChange([{ ...selectedVariant, quantity: 0 }]);
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ProductReplacement({
  defaultSearchTerm = '',
  selectedVariants,
  onChange,
  allLocationsLabel,

  quantityLimit,
  variantLimit = 500, // Shopify default variant limit
  locations,
}: {
  defaultSearchTerm?: string;
  selectedVariants: SelectedVariant[];
  onChange: (selectedVariants: SelectedVariant[]) => void;
  /** Limit the quantity selectable for each variant. Notably not limiting the total quantity selected. */
  quantityLimit?: number;
  variantLimit?: number;
  locations: FulfillmentLocation[];
  allLocationsLabel?: string;
}) {
  return (
    <div className="space-y-4">
      <ProductSearch
        defaultSearchTerm={defaultSearchTerm}
        selectedVariants={selectedVariants}
        onChange={onChange}
        limited={selectedVariants.length >= variantLimit}
        locations={locations}
        allLocationsLabel={allLocationsLabel}
      />

      <SelectedVariantsList
        selectedVariants={selectedVariants}
        onChange={onChange}
        quantityLimit={quantityLimit}
      />

      <div className="flex items-center gap-2">
        <Badge>
          {selectedVariants.length}/{variantLimit} variants selected
        </Badge>
      </div>
    </div>
  );
}
